import TableCell from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableCell';
import {
  FDBox,
  FDSecondaryActionIconButton,
  FDStack,
} from '@facet-wealth/design';
import React from 'react';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  tableCellStyle,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionAssumptionsAndDetails/Layout/ScenarioSelectionDetailedSummaryPicker/ScenarioSelectionDetailedSummary/ScenarioSelectionDetailedSummarySections/ScenarioSelectionDetailedSummaryHelpers';
import useMobileLayout from 'Hooks/useMobileLayout';
import { getBorderRadius } from 'Components/utils';
import { dataAttrs } from 'Helpers/Testing';


const useStyles = buildUseStyles('ScenarioSelectionDetailSummaryStickyCell', (theme) => ({
  root: {
    position: 'sticky',
    left: 0,
    backgroundColor: theme.palette.facet.lightGray,
    zIndex: 1,
    ...tableCellStyle(theme),
  },
  // The purpose of this is to make the whole cell content into a clickable accordion header.
  // We don't have a standalone Collapsible component right now that would have served this purpose.
  clickable: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  withBackground: {
    // Instead of the outer <td> having padding, we're going to make the outer <td> be the background color so that the inner <div> can have the padding and a custom background color.
    // The reason we're doing this complexity is because without it, a user would scroll sideways and the non-sticky columns would go under this one, which would make the left-side rounded corner no longer be rounded.
    padding: theme.spacing(0),
    // Remove the left border radius, or else any custom background of other <td>s will appear in that empty area
    borderTopLeftRadius: '0 !important', // Needs an !important due to a specificity conflict
    borderBottomLeftRadius: '0 !important', // Needs an !important due to a specificity conflict
    '& > *': {
      ...getBorderRadius(theme.radius.md, { all: true }),
    },
  },
  emphasized: {
    // The reason this isn't just a simple background color is due to some visual artifacts in Chrome on the right side.
    // All that matters is that the left side is the background color, so I made the right side be the same color as the
    background: `linear-gradient(90deg, ${theme.palette.facet.lightGray} 50%, ${theme.palette.facet.oceana800} 51%)`,
    '& > *': {
      backgroundColor: theme.palette.facet.oceana800,
    },
  },
  emphasizedDense: {
    '& > *': {
      padding: theme.spacing(0, 0, 0, 0),
    },
  },
  emphasizedNotDense: {
    '& > *': {
      padding: theme.spacing(2, 2, 2, 0),
    },
  },
  white: {
    // See the similar comment for emphasized
    background: `linear-gradient(90deg, ${theme.palette.facet.lightGray} 50%, ${theme.palette.common.white} 51%)`,
    '& > *': {
      padding: theme.spacing(0, 2, 0, 0),
      backgroundColor: theme.palette.common.white,
    },
  },
}));

export const ScenarioSelectionDetailedSummaryStickyCell = ({
  dense,
  isExpandable,
  expanded,
  background,
  showMonths,
  label,
  stickyColumn,
  level,
  test,
}) => {
  const classes = useStyles();
  const isMobile = useMobileLayout();

  const arrowIconButton = isExpandable ? <FDSecondaryActionIconButton icon={expanded ? 'keyboardArrowUp' : 'keyboardArrowDown'} /> : null;

  let maxWidth;
  if (showMonths) {
    maxWidth = isMobile ? '50vw' : undefined;
  }

  return (
    <TableCell
      className={classNames(classes.root, {
        [classes.clickable]: isExpandable,
        [classes.withBackground]: !!background,
        [classes.emphasized]: background === 'emphasized',
        [classes.emphasizedDense]: background === 'emphasized' && dense,
        [classes.emphasizedNotDense]: background === 'emphasized' && !dense,
        [classes.white]: background === 'white',
      })}
    >
      <FDStack
        spacing={1}
        alignItems={'center'}
        minWidth={showMonths && !isMobile ? '400px' : undefined}
        maxWidth={maxWidth}
        {...dataAttrs(test)}
      >
        {level > 0 ? <FDBox paddingX={level} /> : null}
        <FDStack justifyContent={'center'} alignItems={'center'} width={'12px'} height={'40px'}>{arrowIconButton}</FDStack>
        <FDStack flex={1}>{label}</FDStack>
        {stickyColumn ? <FDStack justifyContent="flex-end" {...dataAttrs('sticky-column')}>{stickyColumn}</FDStack> : null}
      </FDStack>
    </TableCell>
  );
};

ScenarioSelectionDetailedSummaryStickyCell.defaultProps = {
  label: null,
  stickyColumn: null,
  isExpandable: false,
  expanded: false,
  showMonths: false,
  level: 0,
  background: undefined,
  test: undefined,
};

ScenarioSelectionDetailedSummaryStickyCell.propTypes = {
  label: PropTypes.node,
  stickyColumn: PropTypes.node,
  expanded: PropTypes.object,
  isExpandable: PropTypes.bool,
  showMonths: PropTypes.bool,
  level: PropTypes.number,
  background: PropTypes.oneOf(['emphasized', 'white']),
  test: PropTypes.string,
  dense: PropTypes.bool.isRequired,
};
